import React, { useEffect, useState } from 'react';
import { Loader, Dimmer } from 'semantic-ui-react';
import FollowUp from '../../../../../../components/FollowUp';
import { getMessages } from '../../../../../../services/followup';
import MessageSender from '../../../../../../components/MessageSender';
import ClaimFollowUpPendencyFields, {
  ClaimPendencyValues
} from '../../../../../../components/ClaimFollowUpPendencyFields';
import { updateClaimPendency } from '../../../../../../services/claim';
import { get } from '../../../../../../services/storage';
import { UserRolesEnum } from '../../../../../../enums/user-roles.enum';
import Button from '../../../../../../components/Button';
import {
  dangerNotification,
  successNotification
} from '../../../../../../services/notification';
import ClaimInfoHeader from '../../ClaimInfoHeader';
import styled from 'styled-components';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';

const SectionLabel = styled.div`
  color: #2f3441;
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 8px;
`;

const PendencySection = styled.div`
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: flex-end;
  gap: 20px;
  margin: 0 0 20px;
  padding: 0 0 20px;

  @media (max-width: 768px) {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }
`;

const PendencyActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;

  @media (max-width: 768px) {
    justify-content: flex-end;
  }
`;

const ClearPendencyButton = styled.button`
  align-items: center;
  background: #aeb3ba;
  border: 0;
  border-radius: 4px;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  font-size: 18px;
  height: 32px;
  justify-content: center;
  line-height: 1;
  padding: 0;
  width: 32px;

  &:hover {
    background: #8f959d;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

export type FollowUpModalProps = {
  id: string;
  claim: any;
  isBlocked?: boolean;
  onPendencyUpdated?: (pendency: ClaimPendencyValues) => void;
};

const FollowUpTab = (props: FollowUpModalProps) => {
  const { id, claim } = props; // , setUnread, unread
  const [messages, setMessages] = useState<any[]>([]);
  const [totalMessages, setTotalMessages] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(1);

  const userRole = get('role');
  const canManagePendencyFields =
    userRole === UserRolesEnum.brokerAdmin || userRole === UserRolesEnum.brokerAnalyst;

  const getClaimPendency = (): ClaimPendencyValues => ({
    responsible: claim.pendingResponsible || undefined,
    type: claim.pendingType || undefined
  });

  const [currentPendency, setCurrentPendency] = useState<ClaimPendencyValues>(
    getClaimPendency()
  );
  const [draftPendency, setDraftPendency] = useState<ClaimPendencyValues>(
    getClaimPendency()
  );
  const [isPendencySaving, setIsPendencySaving] = useState<boolean>(false);

  const isPendencyDirty =
    currentPendency.responsible !== draftPendency.responsible ||
    currentPendency.type !== draftPendency.type;

  const handleMessages = async (requestedPage = page) => {
    const response = await getMessages(id, requestedPage);
    const { data, count } = response;
    setMessages([...data]);
    setTotalMessages(count);
    setPage(requestedPage);
    setIsLoading(false);
  };

  useEffect(() => {
    handleMessages();
  }, [id]);

  useEffect(() => {
    const nextPendency = getClaimPendency();
    setCurrentPendency(nextPendency);
    setDraftPendency(nextPendency);
  }, [claim.pendingResponsible, claim.pendingType]);

  const handleConfirmPendency = async () => {
    if (!isPendencyDirty || isPendencySaving || !canManagePendencyFields) return;

    try {
      setIsPendencySaving(true);
      await updateClaimPendency(id, {
        pending_responsible: draftPendency.responsible || null,
        pending_type: draftPendency.type || null
      });
      setCurrentPendency(draftPendency);
      if (props.onPendencyUpdated) {
        props.onPendencyUpdated(draftPendency);
      }
      await handleMessages(1);
      successNotification('Sucesso!', 'Status de pendência atualizado.');
    } catch (error) {
      console.error('ERRO AO ATUALIZAR STATUS DE PENDÊNCIA:', error);
      dangerNotification(
        'Oops...',
        'Não foi possível atualizar o status de pendência, tente novamente mais tarde.'
      );
    } finally {
      setIsPendencySaving(false);
    }
  };

  const handleFollowupMessageSent = () => {
    if (currentPendency.responsible === 'Imobiliária') {
      const nextPendency: ClaimPendencyValues = {
        ...currentPendency,
        responsible: 'Corretora'
      };
      setCurrentPendency(nextPendency);
      setDraftPendency(nextPendency);
      if (props.onPendencyUpdated) {
        props.onPendencyUpdated(nextPendency);
      }
    }

    handleMessages(1).catch(error => {
      console.error('ERRO AO RECARREGAR FOLLOWUPS:', error);
    });
  };

  return (
    <>
      {isLoading ? (
        <Dimmer active={isLoading} inverted>
          <Loader content="Carregando mensagens..." />
        </Dimmer>
      ) : (
        <>
          <ClaimInfoHeader claim={claim} />
          <PendencySection>
            <ClaimFollowUpPendencyFields
              values={draftPendency}
              disabled={isPendencySaving || !canManagePendencyFields || props.isBlocked}
              onChange={setDraftPendency}
            />
            <PendencyActions>
              <Button
                primary
                content="Confirmar"
                onClick={handleConfirmPendency}
                loading={isPendencySaving}
                disabled={
                  isPendencySaving ||
                  !canManagePendencyFields ||
                  props.isBlocked ||
                  !isPendencyDirty
                }
              />
              <ClearPendencyButton
                type="button"
                title="Limpar pendência"
                aria-label="Limpar pendência"
                onClick={() => setDraftPendency({})}
                disabled={
                  isPendencySaving ||
                  !canManagePendencyFields ||
                  props.isBlocked ||
                  (!draftPendency.responsible && !draftPendency.type)
                }
              >
                ×
              </ClearPendencyButton>
            </PendencyActions>
          </PendencySection>
          <SectionLabel>Followup</SectionLabel>
          {messages.length > 0 ? (
            <FollowUp
              key={messages[0].id || 'claim-followup'}
              messages={messages}
              id={id}
              isBlocked={props.isBlocked}
              totalMessages={totalMessages}
              page={page}
              setPage={setPage}
              accentColor={CLAIM_UPDATES_ACCENT_COLOR}
              hideSendIcon
              showComposerDivider
              disableOwnScroll
              onMessageSent={handleFollowupMessageSent}
            /> // setUnread={setUnread} unread={unread}
          ) : (
            <MessageSender
              id={id}
              isBlocked={props.isBlocked}
              accentColor={CLAIM_UPDATES_ACCENT_COLOR}
              hideSendIcon
              showComposerDivider
              onMessageSent={handleFollowupMessageSent}
            />
          )}
        </>
      )}
    </>
  );
};

export default FollowUpTab;
